import type {
  Diagnostic,
  DomainObject,
  LossReport,
  ModelDocument,
} from "../shared/contracts.js";
import {
  createModel,
  createObject,
  uid,
  syncDocument,
  assertSafeValue,
} from "../core/model.js";
const record = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === "object" && !Array.isArray(v);
const supported = new Set([
  "bsonType",
  "type",
  "required",
  "properties",
  "additionalProperties",
  "items",
  "additionalItems",
  "minItems",
  "maxItems",
  "uniqueItems",
  "minLength",
  "maxLength",
  "pattern",
  "minimum",
  "maximum",
  "exclusiveMinimum",
  "exclusiveMaximum",
  "multipleOf",
  "minProperties",
  "maxProperties",
  "enum",
  "allOf",
  "anyOf",
  "oneOf",
  "not",
  "dependencies",
  "description",
  "title",
  "patternProperties",
]);
function inspect(
  schema: Record<string, unknown>,
  diagnostics: Diagnostic[],
  path = "$",
  exporting = false,
  depth = 0,
) {
  if (depth > 32) {
    diagnostics.push({
      code: "MONGO_DEPTH_LIMIT",
      severity: "error",
      path,
      message: "Validator exceeds supported depth 32.",
    });
    return;
  }
  for (const [key, value] of Object.entries(schema)) {
    if (!supported.has(key))
      diagnostics.push({
        code: "MONGO_KEYWORD_UNSUPPORTED",
        severity: exporting ? "error" : "warning",
        path: `${path}.${key}`,
        message: `${key} is preserved but not supported by the MongoDB validator profile.`,
      });
    if (key === "properties" || key === "patternProperties") {
      if (!record(value)) {
        diagnostics.push({
          code: "MONGO_SCHEMA_SHAPE",
          severity: "error",
          path: `${path}.${key}`,
          message: "Properties must be an object.",
        });
        continue;
      }
      for (const [name, child] of Object.entries(value))
        if (record(child))
          inspect(
            child,
            diagnostics,
            `${path}.${key}.${name}`,
            exporting,
            depth + 1,
          );
        else
          diagnostics.push({
            code: "MONGO_SCHEMA_SHAPE",
            severity: "error",
            path,
            message: "Each property must have an object schema.",
          });
    } else if (
      ["items", "additionalProperties", "additionalItems", "not"].includes(
        key,
      ) &&
      record(value)
    )
      inspect(value, diagnostics, `${path}.${key}`, exporting, depth + 1);
    else if (
      ["oneOf", "anyOf", "allOf", "items"].includes(key) &&
      Array.isArray(value)
    )
      value.forEach((v, i) => {
        if (record(v))
          inspect(v, diagnostics, `${path}.${key}[${i}]`, exporting, depth + 1);
      });
    if (key === "dependencies") {
      if (!record(value))
        diagnostics.push({
          code: "MONGO_SCHEMA_SHAPE",
          severity: "error",
          path,
          message: "Dependencies must be an object.",
        });
      else
        for (const [name, dependency] of Object.entries(value)) {
          if (record(dependency))
            inspect(
              dependency,
              diagnostics,
              `${path}.dependencies.${name}`,
              exporting,
              depth + 1,
            );
          else if (
            !Array.isArray(dependency) ||
            dependency.some((v) => typeof v !== "string")
          )
            diagnostics.push({
              code: "MONGO_SCHEMA_SHAPE",
              severity: "error",
              path,
              message:
                "A dependency must be a schema or an array of field names.",
            });
        }
    }
    if (
      ["allOf", "anyOf", "oneOf"].includes(key) &&
      (!Array.isArray(value) ||
        value.length === 0 ||
        value.some((v) => !record(v)))
    )
      diagnostics.push({
        code: "MONGO_SCHEMA_SHAPE",
        severity: "error",
        path,
        message: `${key} must be a nonempty array of object schemas.`,
      });
    if (
      ["additionalProperties", "additionalItems"].includes(key) &&
      typeof value !== "boolean" &&
      !record(value)
    )
      diagnostics.push({
        code: "MONGO_SCHEMA_SHAPE",
        severity: "error",
        path,
        message: `${key} must be a boolean or a schema.`,
      });
    if (
      ["exclusiveMinimum", "exclusiveMaximum", "uniqueItems"].includes(key) &&
      typeof value !== "boolean"
    )
      diagnostics.push({
        code: "MONGO_SCHEMA_SHAPE",
        severity: "error",
        path,
        message: `${key} uses a boolean in MongoDB's draft 4 profile.`,
      });
    if (
      ["minimum", "maximum", "multipleOf"].includes(key) &&
      (typeof value !== "number" ||
        !Number.isFinite(value) ||
        (key === "multipleOf" && value <= 0))
    )
      diagnostics.push({
        code: "MONGO_SCHEMA_SHAPE",
        severity: "error",
        path,
        message: `${key} requires a finite numeric bound (positive for multipleOf).`,
      });
    if (
      [
        "minLength",
        "maxLength",
        "minItems",
        "maxItems",
        "minProperties",
        "maxProperties",
      ].includes(key) &&
      (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0)
    )
      diagnostics.push({
        code: "MONGO_SCHEMA_SHAPE",
        severity: "error",
        path,
        message: `${key} requires a nonnegative integer.`,
      });
    if (
      ["pattern", "description", "title"].includes(key) &&
      typeof value !== "string"
    )
      diagnostics.push({
        code: "MONGO_SCHEMA_SHAPE",
        severity: "error",
        path,
        message: `${key} requires a string.`,
      });
    if (key === "type") {
      const types = Array.isArray(value) ? value : [value];
      if (
        !types.length ||
        new Set(types).size !== types.length ||
        types.some(
          (t) =>
            ![
              "object",
              "array",
              "number",
              "boolean",
              "string",
              "null",
              "integer",
            ].includes(String(t)),
        )
      )
        diagnostics.push({
          code: "MONGO_JSON_TYPE",
          severity: "error",
          path,
          message: "Invalid JSON type declaration.",
        });
    }
    if (
      key === "required" &&
      (!Array.isArray(value) ||
        value.some((x) => typeof x !== "string") ||
        new Set(value).size !== value.length)
    )
      diagnostics.push({
        code: "MONGO_REQUIRED_INVALID",
        severity: "error",
        path,
        message: "Required must be an array of distinct field names.",
      });
    if (key === "bsonType") {
      const types = Array.isArray(value) ? value : [value],
        allowed = [
          "object",
          "array",
          "double",
          "string",
          "binData",
          "undefined",
          "objectId",
          "bool",
          "date",
          "null",
          "regex",
          "dbPointer",
          "javascript",
          "symbol",
          "javascriptWithScope",
          "int",
          "timestamp",
          "long",
          "decimal",
          "minKey",
          "maxKey",
        ];
      if (types.some((x) => typeof x !== "string" || !allowed.includes(x)))
        diagnostics.push({
          code: "MONGO_BSON_TYPE",
          severity: "error",
          path,
          message: "Unknown BSON type; do not coerce it to JSON number.",
        });
    }
    if (
      key === "type" &&
      (value === "integer" ||
        (Array.isArray(value) && value.includes("integer")))
    )
      diagnostics.push({
        code: "MONGO_INTEGER_TYPE",
        severity: "error",
        path,
        message:
          "Use bsonType int or long; MongoDB does not support JSON Schema integer.",
      });
  }
}
export function importMongo(text: string): {
  document: ModelDocument;
  diagnostics: Diagnostic[];
  lossReport: LossReport;
} {
  const document = createModel("Document schema", "physical", "document"),
    diagnostics: Diagnostic[] = [],
    lossReport: LossReport = {
      preserved: [],
      translated: [],
      preservedOpaque: [],
      lost: [],
      unresolved: [],
    };
  let input: unknown;
  try {
    if (text.length > 10 * 1024 * 1024)
      throw new Error("Document validator exceeds 10 MiB.");
    input = JSON.parse(text);
    assertSafeValue(input);
  } catch (e) {
    diagnostics.push({
      code: "MONGO_INVALID_JSON",
      severity: "error",
      message: (e as Error).message,
    });
    return { document, diagnostics, lossReport };
  }
  const entries =
    record(input) && Array.isArray(input.collections)
      ? input.collections
      : [input];
  if (entries.length > 1000) {
    diagnostics.push({
      code: "MONGO_COLLECTION_LIMIT",
      severity: "error",
      message: "At most 1,000 collection validators per import.",
    });
    return { document, diagnostics, lossReport };
  }
  const rep = document.model.activeRepresentation;
  const addProperties = (
    schema: Record<string, unknown>,
    owner: DomainObject,
    depth = 0,
  ) => {
    if (depth > 32) return;
    const props = record(schema.properties) ? schema.properties : {};
    const required = Array.isArray(schema.required) ? schema.required : [];
    Object.entries(props).forEach(([name, value], ordinal) => {
      if (!record(value)) return;
      const field = createObject("property", name, rep, owner.objectId);
      field.documentSchema = structuredClone(value);
      field.ordinal = ordinal;
      const declared = value.bsonType ?? value.type;
      const types = Array.isArray(declared)
        ? declared
        : [declared ?? "unknown"];
      field.type = { name: String(types.find((t) => t !== "null") ?? "null") };
      field.nullable = types.includes("null");
      field.extensions["dataglow.document"] = {
        required: required.includes(name),
        originalName: name,
        originalType: field.type.name,
        originalNullable: field.nullable,
      };
      document.objects.push(field);
      if (record(value.properties)) addProperties(value, field, depth + 1);
      else if (record(value.items) && record(value.items.properties)) {
        field.extensions["dataglow.document"] = {
          ...(field.extensions["dataglow.document"] as object),
          childrenInItems: true,
        };
        addProperties(value.items, field, depth + 1);
      }
    });
  };
  for (const [i, entry] of entries.entries()) {
    if (!record(entry)) {
      diagnostics.push({
        code: "MONGO_SCHEMA_SHAPE",
        severity: "error",
        message: "Expected a validator object.",
      });
      continue;
    }
    const validator = record(entry.validator) ? entry.validator : entry;
    const schema = record(validator.$jsonSchema)
      ? validator.$jsonSchema
      : validator;
    if (!(
      "bsonType" in schema ||
      "properties" in schema ||
      "type" in schema ||
      "oneOf" in schema ||
      "anyOf" in schema
    )) {
      diagnostics.push({
        code: "MONGO_SAMPLE_NOT_SCHEMA",
        severity: "error",
        message:
          "This input is not a declared validator. Sample inference is a separate unavailable operation.",
      });
      continue;
    }
    inspect(schema, diagnostics);
    const collection = createObject(
      "collection",
      typeof entry.name === "string" ? entry.name : `collection_${i + 1}`,
      rep,
    );
    collection.documentSchema = structuredClone(schema);
    collection.extensions["dataglow.document"] = {
      validationLevel: entry.validationLevel ?? "strict",
      validationAction: entry.validationAction ?? "error",
      indexes: Array.isArray(entry.indexes)
        ? structuredClone(entry.indexes)
        : [],
      source: "declared-validator",
      originalSchema: structuredClone(schema),
      outerValidator:
        validator !== schema
          ? Object.fromEntries(
              Object.entries(validator).filter(([k]) => k !== "$jsonSchema"),
            )
          : {},
    };
    document.objects.push(collection);
    addProperties(schema, collection);
    lossReport.preserved.push(
      `${collection.name}: BSON types, nested structure, requiredness, variants and validation clauses`,
    );
  }
  document.diagrams[0].appearances = document.objects
    .filter((o) => o.kind === "collection")
    .map((o, i) => ({
      appearanceId: uid(),
      objectId: o.objectId,
      position: { x: 80 + i * 340, y: 100 },
    }));
  syncDocument(document);
  for (const d of diagnostics.filter(
    (d) => d.code === "MONGO_KEYWORD_UNSUPPORTED",
  ))
    lossReport.preservedOpaque.push(`${d.path}: ${d.message}`);
  return { document, diagnostics, lossReport };
}
export function exportMongo(document: ModelDocument): {
  json: string;
  diagnostics: Diagnostic[];
} {
  const diagnostics: Diagnostic[] = [];
  if (document.model.family !== "document")
    return {
      json: "",
      diagnostics: [
        {
          code: "MONGO_MODEL_FAMILY",
          severity: "error",
          message: "Choose a document model or derive one explicitly.",
        },
      ],
    };
  const children = (id: string) =>
    document.objects
      .filter((o) => o.kind === "property" && o.ownerObjectId === id)
      .sort((a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0));
  const rebuild = (
    schema: Record<string, unknown>,
    owner: DomainObject,
    depth = 0,
  ): Record<string, unknown> => {
    const result = structuredClone(schema);
    if (depth > 32) {
      diagnostics.push({
        code: "MONGO_DEPTH_LIMIT",
        severity: "error",
        objectId: owner.objectId,
        message: "Property tree exceeds depth 32.",
      });
      return result;
    }
    const fields = children(owner.objectId);
    if (fields.length || record(result.properties)) {
      const properties: Record<string, unknown> = {},
        required: string[] = Array.isArray(result.required)
          ? result.required.filter(
              (name): name is string =>
                typeof name === "string" &&
                (!record(result.properties) || !(name in result.properties)),
            )
          : [];
      const seen = new Set<string>();
      for (const field of fields) {
        if (seen.has(field.name)) {
          diagnostics.push({
            code: "MONGO_DUPLICATE_PATH",
            severity: "error",
            objectId: field.objectId,
            message: "Duplicate property name under one parent.",
          });
          continue;
        }
        seen.add(field.name);
        const ext = (field.extensions["dataglow.document"] ?? {}) as Record<
          string,
          unknown
        >;
        let f = structuredClone(field.documentSchema ?? {});
        const original = f.bsonType ?? f.type;
        const typeChanged =
          field.type?.name !== undefined &&
          field.type.name !== ext.originalType;
        if (original !== undefined || typeChanged) {
          let types = Array.isArray(original)
            ? [...original]
            : [original ?? field.type?.name ?? "string"];
          if (typeChanged) types = [field.type!.name];
          types = types.filter((t) => t !== "null");
          if (field.nullable) types.push("null");
          if (types.length)
            f["bsonType" in f || !("type" in f) ? "bsonType" : "type"] =
              types.length === 1 ? types[0] : types;
        } else if (field.nullable !== ext.originalNullable)
          diagnostics.push({
            code: "MONGO_VARIANT_NULLABILITY",
            severity: "error",
            objectId: field.objectId,
            message:
              "Edit the variant schema explicitly to change nullability of a property without a declared type.",
          });
        if (ext.childrenInItems) {
          f.items = rebuild(
            record(f.items) ? f.items : { bsonType: "object" },
            field,
            depth + 1,
          );
        } else if (children(field.objectId).length || record(f.properties))
          f = rebuild(f, field, depth + 1);
        properties[field.name] = f;
        if (ext.required === true) required.push(field.name);
      }
      result.properties = properties;
      if (required.length) result.required = required;
      else delete result.required;
    }
    return result;
  };
  const collections = document.objects
    .filter((o) => o.kind === "collection")
    .map((collection) => {
      const extBefore = (collection.extensions["dataglow.document"] ??
        {}) as Record<string, unknown>;
      const authoredSchema = collection.documentSchema ?? {
        bsonType: "object",
      };
      const schemaChanged =
        extBefore.originalSchema !== undefined &&
        JSON.stringify(extBefore.originalSchema) !==
          JSON.stringify(authoredSchema);
      const schema =
        schemaChanged ||
        (!children(collection.objectId).length &&
          extBefore.originalSchema === undefined)
          ? structuredClone(authoredSchema)
          : rebuild(authoredSchema, collection);
      if (schemaChanged)
        diagnostics.push({
          code: "MONGO_SCHEMA_PROJECTION_STALE",
          severity: "warning",
          objectId: collection.objectId,
          message:
            "Authored collection schema changed; its full schema is exported. Re-import to refresh projected field objects.",
        });
      inspect(schema, diagnostics, collection.name, true);
      const ext = (collection.extensions["dataglow.document"] ?? {}) as Record<
        string,
        unknown
      >;
      if (
        schema.additionalProperties === false &&
        record(schema.properties) &&
        !("_id" in schema.properties)
      )
        diagnostics.push({
          code: "MONGO_CLOSED_ID",
          severity: "warning",
          objectId: collection.objectId,
          message:
            "Closed root schema omits _id; MongoDB-generated documents will fail validation.",
        });
      return {
        name: collection.name,
        validator: {
          ...(record(ext.outerValidator) ? ext.outerValidator : {}),
          $jsonSchema: schema,
        },
        validationLevel: ext.validationLevel ?? "strict",
        validationAction: ext.validationAction ?? "error",
        indexes: ext.indexes ?? [],
      };
    });
  if (!collections.length)
    diagnostics.push({
      code: "MONGO_COLLECTION_REQUIRED",
      severity: "error",
      message: "The model has no collections.",
    });
  return {
    json: diagnostics.some((d) => d.severity === "error")
      ? ""
      : JSON.stringify(
          {
            format: "dataglow.mongodb-validator-bundle",
            version: "1.0",
            collections,
          },
          null,
          2,
        ) + "\n",
    diagnostics,
  };
}
